import React from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { Recipe } from '../../types/recipe';
import { IconButton } from '../ui/Button';
import styles from './RecipeCard.module.css';

interface RecipeCardProps {
  recipe: Recipe;
  onEdit?: (recipe: Recipe) => void;
  onDelete?: (recipe: Recipe) => void;
  onClick?: (recipe: Recipe) => void;
}

export const RecipeCard: React.FC<RecipeCardProps> = ({
  recipe,
  onEdit,
  onDelete,
  onClick,
}) => {
  const totalTime = (recipe.prepTime ?? 0) + (recipe.cookTime ?? 0);
  const description = recipe.description?.trim();
  const detailItems = [
    totalTime > 0 ? `${totalTime} min total` : `${recipe.instructions.length} ${recipe.instructions.length === 1 ? 'step' : 'steps'}`,
    recipe.servings ? `Serves ${recipe.servings}` : null,
    `${recipe.ingredients.length} ${recipe.ingredients.length === 1 ? 'ingredient' : 'ingredients'}`,
  ].filter((item): item is string => Boolean(item));

  const handleClick = () => {
    onClick?.(recipe);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick || event.target !== event.currentTarget) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick(recipe);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(recipe);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this recipe?')) {
      onDelete?.(recipe);
    }
  };

  return (
    <div
      className={[styles.card, onClick ? styles.cardInteractive : ''].filter(Boolean).join(' ')}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className={styles.imageContainer}>
        {recipe.imageUrl ? (
          <img src={recipe.imageUrl} alt={recipe.title} className={styles.image} />
        ) : (
          <div className={styles.placeholder}>
            {recipe.title.charAt(0)}
          </div>
        )}
      </div>
      <div className={styles.content}>
        <div className={styles.contentHeader}>
          <div className={styles.titleBlock}>
            <h3 className={styles.title}>{recipe.title}</h3>
            {description ? <p className={styles.description}>{description}</p> : null}
          </div>

          {(onEdit || onDelete) && (
            <div className={styles.actions}>
              {onEdit && (
                <IconButton
                  icon={FaEdit({ 'aria-hidden': 'true' })}
                  onClick={handleEdit}
                  variant="ghost"
                  size="sm"
                  aria-label="Edit recipe"
                />
              )}
              {onDelete && (
                <IconButton
                  icon={FaTrash({ 'aria-hidden': 'true' })}
                  onClick={handleDelete}
                  variant="danger"
                  size="sm"
                  aria-label="Delete recipe"
                />
              )}
            </div>
          )}
        </div>

        <div className={styles.metaRow}>
          {detailItems.map((item) => (
            <span key={item} className={styles.metaPill}>
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
